import React, { useState, useRef, useEffect } from "react";
import { useLocation, Route, Routes, useNavigate } from "react-router-dom";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuth } from "./auth/AuthContext";
import bgLogin from "./assets/background-login.jpg";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Row,
  Col,
  Container,
} from "reactstrap";
import axios from "axios";
import { api } from "./config/middleware";
import {
  API_USER_AUTHENTICATION,
  API_BASE,
  API_GETEMPLOYEES,
} from "./config/api";
import logoImg from "assets/logo.png";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mainContent = useRef(null);

  const { login } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const toPublicFileUrl = (rel) => {
    if (!rel) return null;
    const clean = String(rel)
      .replace(/^uploads[\\/]/, "")
      .replace(/\\/g, "/");
    return `${API_BASE.replace(/\/+$/, "")}/files/${clean}`;
  };

  useEffect(() => {
    document.body.classList.add("no-sidebar");
    return () => document.body.classList.remove("no-sidebar");
  }, []);

  useEffect(() => {
    document.body.classList.add("bg-default");
    return () => {
      document.body.classList.remove("bg-default");
    };
  }, []);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }
  }, [location]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/auth") {
        return (
          <Route path={prop.path} element={<prop.component />} key={key} />
        );
      }
      return null;
    });
  };

  const onSignIn = async () => {
    if (isLoading) return; // กันกดซ้ำ

    const u = username.trim();
    const p = password.trim();
    if (!u || !p) {
      setErr("กรุณากรอก Username และ Password");
      return;
    }

    setErr("");
    setIsLoading(true);
    try {
      // 1) Auth ปกติ
      const res = await api.post(API_USER_AUTHENTICATION, {
        USERNAME: u,
        PASSWORD: p,
      });

      const statusText = String(res?.data?.statustext || "").toUpperCase();
      if (statusText !== "SUCCESS") {
        const msg =
          res?.data?.message || res?.data?.error || "เข้าสู่ระบบไม่สำเร็จ";
        throw new Error(msg);
      }

      const rawUser = res?.data?.data?.[0] || {};
      const psId = Number(rawUser?.psId);
      const mapRoleFromPsId = (id) =>
        [1, 2, 3].includes(id) ? "admin" : "user";
      const role = mapRoleFromPsId(psId);

      const accessToken =
        res?.data?.accessToken || res?.data?.token || res?.data?.jwt || null;

      // username ที่ใช้ matching กับพนักงาน
      const loginUsername = rawUser.empUsername ?? rawUser.EMPUSERNAME ?? u;

      // ====== 2) ยิง API_GETEMPLOYEES เพื่อดึงรูปจากตาราง Employee ======
      let avatarUrl = null;

      const orgId = rawUser.orgId ?? rawUser.ORGID ?? null;
      if (orgId) {
        try {
          const empRes = await api.get(API_GETEMPLOYEES, {
            params: { ORGID: orgId },
          });

          const raw =
            empRes?.data?.data?.rows ??
            empRes?.data?.rows ??
            empRes?.data?.data ??
            empRes?.data ??
            [];

          const list = Array.isArray(raw) ? raw : [];

          const me = list.find((emp) => {
            const empUsername =
              emp.EMPUSERNAME ?? emp.empUsername ?? emp.username ?? "";
            return (
              String(empUsername).toLowerCase() ===
              String(loginUsername).toLowerCase()
            );
          });

          if (me) {
            const picRel = me.EMPPICTURE ?? me.empPicture ?? null;

            if (picRel) {
              avatarUrl = toPublicFileUrl(picRel);
            }
          }
        } catch (e) {
          console.warn("โหลดข้อมูลพนักงานเพื่อดึงรูปไม่สำเร็จ:", e);
        }
      }

      // ====== 3) สร้าง userObj สำหรับ AuthContext / zustand ======
      const userObj = {
        ...rawUser,
        psId,
        role,
        username: loginUsername,
        avatar: avatarUrl || null,
      };

      // 4) เซฟลง AuthContext + zustand
      login(accessToken, role, userObj);

      navigate("/index", { replace: true });
    } catch (error) {
      console.error("Auth failed:", error);
      setErr(
        error?.response?.data?.message ||
          error?.message ||
          "เข้าสู่ระบบล้มเหลว",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <>
      <div
        className="main-content login-bg d-flex align-items-center justify-content-center"
        ref={mainContent}
        style={{
          minHeight: "100vh",
          width: "100%",
          backgroundImage: `url(${bgLogin})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.50)",
            zIndex: 0,
          }}
        />
        {/* Login Card */}
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg="5" md="7">
              <Card
                className="bg-secondary shadow border-0"
                style={{ zIndex: 1, width: "100%", maxWidth: 1080 }}
              >
                <CardBody className="px-lg-5">
                  <Row className="justify-content-center text-center mb-3">
                    <Col>
                      <div className="px-4 py-4 text-center">
                        <img
                          src={logoImg}
                          alt="logo"
                          style={{ height: 80, maxHeight: "100%" }}
                        />
                        <p
                          className="mt-3"
                          style={{
                            fontSize: "clamp(18px, 2vw, 22px)",
                            fontWeight: 500,
                          }}
                        >
                          ระบบจัดการข้อมูลร้านซักรีด
                        </p>
                      </div>
                    </Col>
                  </Row>
                  {err && (
                    <div className="text-danger text-center mb-3">{err}</div>
                  )}
                  <Form
                    role="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSignIn();
                    }}
                  >
                    <FormGroup className="mb-3">
                      <label className="form-control-label mb-1">
                        Username
                      </label>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-email-83" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          autoComplete="off"
                          autoCorrect="off"
                          spellCheck="false"
                        />
                      </InputGroup>
                    </FormGroup>

                    <FormGroup>
                      <label className="form-control-label mb-1">
                        Password
                      </label>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-lock-circle-open" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                          autoCorrect="off"
                          spellCheck="false"
                        />
                      </InputGroup>
                    </FormGroup>

                    <div className="text-center d-flex justify-content-center">
                      <Button
                        className="my-4"
                        color="primary"
                        type="submit"
                        style={{ minWidth: 160 }}
                      >
                        เข้าสู่ระบบ
                      </Button>
                    </div>

                    <Row className="justify-content-center">
                      <div className="d-flex align-items-center text-center">
                        <span className="mr-1 small">
                          คุณยังไม่มีบัญชีเข้าใช้งาน?
                        </span>
                        <Button
                          color="link"
                          className="p-0 align-baseline"
                          type="button"
                          onClick={() => navigate("/register")}
                        >
                          สมัครใช้งาน
                        </Button>
                      </div>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Login;
